import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/api';

// Async thunks
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async ({ tree = false } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (tree) params.append('tree', 'true');

      const response = await apiFetch(`/merchants/categories?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch categories');
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch categories');
    }
  }
);

export const fetchCategory = createAsyncThunk(
  'categories/fetchCategory',
  async ({ categoryId, includeProducts = false }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (includeProducts) params.append('includeProducts', 'true');

      const response = await apiFetch(`/merchants/categories/${categoryId}?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch category');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch category');
    }
  }
);

export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await apiFetch('/merchants/categories', {
        method: 'POST',
        body: JSON.stringify(categoryData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create category');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create category');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ id, data: updateData }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update category');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update category');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/categories/${categoryId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete category');
      return categoryId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete category');
    }
  }
);

export const reorderCategories = createAsyncThunk(
  'categories/reorderCategories',
  async (categoryOrders, { rejectWithValue }) => {
    try {
      const response = await apiFetch('/merchants/categories/reorder', {
        method: 'PUT',
        body: JSON.stringify({ categoryOrders })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to reorder categories');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reorder categories');
    }
  }
);

export const createDefaultCategories = createAsyncThunk(
  'categories/createDefaults',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch('/merchants/categories/create-defaults', {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create default categories');
      return data.data || { created: [] };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create default categories');
    }
  }
);

export const assignProductsToCategory = createAsyncThunk(
  'categories/assignProducts',
  async ({ categoryId, productIds }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/categories/${categoryId}/products`, {
        method: 'POST',
        body: JSON.stringify({ productIds })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to assign products');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to assign products');
    }
  }
);

export const removeProductsFromCategory = createAsyncThunk(
  'categories/removeProducts',
  async ({ categoryId, productIds }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/categories/${categoryId}/products`, {
        method: 'DELETE',
        body: JSON.stringify({ productIds })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to remove products');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to remove products');
    }
  }
);

const initialState = {
  categories: [],
  currentCategory: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  total: 0
};

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.categories = action.payload;
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch single category
      .addCase(fetchCategory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCategory = action.payload;
        state.error = null;
      })
      .addCase(fetchCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Create category
      .addCase(createCategory.fulfilled, (state, action) => {
        // If it's a subcategory, add to parent's children
        if (action.payload.parentId) {
          const parentIndex = state.categories.findIndex(c => c.id === action.payload.parentId);
          if (parentIndex !== -1) {
            if (!state.categories[parentIndex].children) {
              state.categories[parentIndex].children = [];
            }
            state.categories[parentIndex].children.push(action.payload);
          }
        } else {
          // Add to root categories
          state.categories.push(action.payload);
        }
        state.currentCategory = action.payload;
      })

      // Update category
      .addCase(updateCategory.fulfilled, (state, action) => {
        const updated = action.payload;

        // Update in root categories
        const rootIndex = state.categories.findIndex(c => c.id === updated.id);
        if (rootIndex !== -1) {
          state.categories[rootIndex] = updated;
        } else {
          // Check in children of each parent
          state.categories.forEach(parent => {
            if (parent.children) {
              const childIndex = parent.children.findIndex(c => c.id === updated.id);
              if (childIndex !== -1) {
                parent.children[childIndex] = updated;
              }
            }
          });
        }

        state.currentCategory = updated;
      })

      // Delete category
      .addCase(deleteCategory.fulfilled, (state, action) => {
        const deletedId = action.payload;

        // Remove from root categories
        state.categories = state.categories.filter(c => c.id !== deletedId);

        // Also remove from children of each parent
        state.categories.forEach(parent => {
          if (parent.children) {
            parent.children = parent.children.filter(c => c.id !== deletedId);
          }
        });

        if (state.currentCategory?.id === deletedId) {
          state.currentCategory = null;
        }
      })

      // Create defaults
      .addCase(createDefaultCategories.fulfilled, (state, action) => {
        if (action.payload.created && action.payload.created.length > 0) {
          state.categories = [...state.categories, ...action.payload.created];
        }
      });
  }
});

export const { clearCurrentCategory, clearError } = categoriesSlice.actions;

// Selectors
export const selectCategories = (state) => state.categories.categories;
export const selectCurrentCategory = (state) => state.categories.currentCategory;
export const selectCategoriesStatus = (state) => state.categories.status;
export const selectCategoriesError = (state) => state.categories.error;

// Helper selector to get flat list of all categories (including children)
export const selectAllCategoriesFlat = (state) => {
  const result = [];
  state.categories.categories.forEach(cat => {
    result.push(cat);
    if (cat.children) {
      cat.children.forEach(child => {
        result.push({ ...child, parentName: cat.name });
      });
    }
  });
  return result;
};

export default categoriesSlice.reducer;
